import React from 'react';
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink,
  PaginationNext, PaginationPrevious, PaginationEllipsis,
} from '@/components/ui/pagination';

// Builds a compact page list with ellipsis, e.g. 1 … 4 5 [6] 7 8 … 20
function buildPages(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push('ellipsis');
    out.push(p);
    prev = p;
  }
  return out;
}

/**
 * Client-side table pager. Renders a "Showing X–Y of Z" summary and page controls.
 * Hidden when everything fits on one page.
 */
export function TablePagination({ page, pageSize, total, onPageChange, testid = 'table-pagination' }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const go = (p) => onPageChange(Math.min(totalPages, Math.max(1, p)));

  return (
    <div
      className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      data-testid={testid}
    >
      <p className="text-sm text-muted-foreground" data-testid={`${testid}-summary`}>
        Showing <span className="font-medium text-foreground">{from}</span>–
        <span className="font-medium text-foreground">{to}</span> of{' '}
        <span className="font-medium text-foreground">{total}</span>
      </p>
      <Pagination className="mx-0 w-auto justify-end">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              href="#"
              data-testid={`${testid}-prev`}
              aria-disabled={page <= 1}
              className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
              onClick={(e) => { e.preventDefault(); go(page - 1); }}
            />
          </PaginationItem>
          {buildPages(page, totalPages).map((p, i) =>
            p === 'ellipsis' ? (
              <PaginationItem key={`e-${i}`}><PaginationEllipsis /></PaginationItem>
            ) : (
              <PaginationItem key={p}>
                <PaginationLink
                  href="#"
                  isActive={p === page}
                  data-testid={`${testid}-page-${p}`}
                  onClick={(e) => { e.preventDefault(); go(p); }}
                >
                  {p}
                </PaginationLink>
              </PaginationItem>
            )
          )}
          <PaginationItem>
            <PaginationNext
              href="#"
              data-testid={`${testid}-next`}
              aria-disabled={page >= totalPages}
              className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
              onClick={(e) => { e.preventDefault(); go(page + 1); }}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
